// Auto-fit text inside a fixed box. Used by stickies so the content is never
// hidden / ellipsized — text shrinks until it fits, with a floor at
// MIN_FONT_SIZE. The user always sees the full content; if it's too small
// to read, they zoom in or resize the sticky.
//
// Measurement uses an offscreen 2D canvas context (no DOM) so it runs cheap
// during the Konva render pass. We binary-search the largest font size that
// satisfies the line-count ceiling for the given inner box.

import { MIN_FONT_SIZE } from './textStyle';

let ctx: CanvasRenderingContext2D | null = null;

function getCtx(): CanvasRenderingContext2D | null {
  if (ctx) return ctx;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  ctx = c.getContext('2d');
  return ctx;
}

// Count the wrapped lines for `text` at `fontSize` inside `innerWidth`.
// `\n` forces a new paragraph; spaces are soft break points. A word longer
// than innerWidth is broken character by character so it never overflows.
function wrappedLineCount(
  text: string,
  innerWidth: number,
  fontSize: number,
  fontFamily: string,
  fontWeight: 'normal' | 'bold',
  fontStyle: 'normal' | 'italic',
): number {
  if (!text) return 1;
  const c = getCtx();
  if (!c) return text.split('\n').length;
  c.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

  let total = 0;
  for (const paragraph of text.split('\n')) {
    if (paragraph === '') {
      total += 1;
      continue;
    }
    // Soft-break on whitespace; preserve trailing spaces by splitting with
    // a capturing regex so we re-build the line with original spacing.
    const tokens = paragraph.split(/(\s+)/);
    let line = '';
    for (const tok of tokens) {
      const candidate = line + tok;
      if (c.measureText(candidate).width <= innerWidth) {
        line = candidate;
        continue;
      }
      // tok itself doesn't fit on this line. Commit the current line first.
      if (line.length > 0) {
        total += 1;
        line = '';
      }
      // tok is a single very long word — char-wrap it.
      if (c.measureText(tok).width > innerWidth) {
        let chunk = '';
        for (const ch of tok) {
          if (c.measureText(chunk + ch).width <= innerWidth) {
            chunk += ch;
          } else {
            if (chunk.length > 0) total += 1;
            chunk = ch;
          }
        }
        line = chunk;
      } else {
        line = tok;
      }
    }
    if (line.length > 0) total += 1;
  }
  return Math.max(1, total);
}

interface FitOptions {
  innerWidth: number;
  innerHeight: number;
  // Largest font size to try. The fitted result is at most this value (it
  // never grows beyond the user's intent).
  baseFontSize: number;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  lineHeight?: number;
}

// Largest integer font size that fits `text` in `(innerWidth × innerHeight)`.
// Returns at least MIN_FONT_SIZE so the result is always renderable.
export function fitFontSize(text: string, opts: FitOptions): number {
  const lineHeight = opts.lineHeight ?? 1.4;
  if (opts.innerWidth <= 0 || opts.innerHeight <= 0) return MIN_FONT_SIZE;
  if (!text) return opts.baseFontSize;

  const fontWeight: 'normal' | 'bold' = opts.bold ? 'bold' : 'normal';
  const fontStyle: 'normal' | 'italic' = opts.italic ? 'italic' : 'normal';

  const fits = (size: number) => {
    const lines = wrappedLineCount(text, opts.innerWidth, size, opts.fontFamily, fontWeight, fontStyle);
    return lines * size * lineHeight <= opts.innerHeight;
  };

  // Quick out: the base size already fits.
  if (fits(opts.baseFontSize)) return opts.baseFontSize;

  // Binary search [MIN_FONT_SIZE, baseFontSize] for the largest size that
  // fits. Stops in O(log n) iterations.
  let lo = MIN_FONT_SIZE;
  let hi = opts.baseFontSize;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi + 1) / 2);
    if (fits(mid)) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(MIN_FONT_SIZE, lo);
}
